/**
 * Rozbor podkladů z úvodního kroku.
 *
 * /api/rozbor přečte závěrky a zadání jednou, /api/rozbor/vyhodnot pak po
 * každé odpovědi přepočítá dotazy a metodu bez AI a bez nahrávání souborů.
 */
import { authHeaders } from '../lib/apiFetch';

const API_BASE = process.env.REACT_APP_API_BASE || '';

async function chybaZOdpovedi(res) {
  const err = await res.json().catch(() => ({}));
  return new Error(err.detail || `HTTP ${res.status}`);
}

export async function spustRozbor({ soubory, zadani, typPodnikani }) {
  const fd = new FormData();
  soubory.forEach(f => fd.append('files', f));
  fd.append('zadani', JSON.stringify({
    text: zadani.text || '',
    podklady: (zadani.soubory || []).map(s => ({ nazev: s.name || '', komentar: s.komentar || '' })),
  }));
  if (typPodnikani) fd.append('typ_podnikani', typPodnikani);
  const res = await fetch(`${API_BASE}/api/rozbor`, { method: 'POST', headers: await authHeaders(), body: fd });
  if (!res.ok) throw await chybaZOdpovedi(res);
  return res.json();
}

export async function vyhodnotRozbor({ vykaz, typPodnikani, odpovedi }) {
  const res = await fetch(`${API_BASE}/api/rozbor/vyhodnot`, {
    method: 'POST',
    headers: await authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ vykaz, typ_podnikani: typPodnikani || null, odpovedi }),
  });
  if (!res.ok) throw await chybaZOdpovedi(res);
  return res.json();
}

const prazdne = v => v === '' || v === null || v === undefined;

/**
 * Propíše potvrzené odpovědi do dalších kroků wizardu.
 *
 * Vyplní jen prázdná pole, nic, co klient zadal ručně, nepřepíše. Krok
 * Očištění zisku pracuje s měsíční odměnou, proto se roční částky dělí
 * dvanácti. Kdo bude firmu po prodeji řídit, rozhoduje klient až tam.
 */
export function prevezmiOdpovedi(stav, odpovedi) {
  const o = odpovedi || {};
  const dalsi = { ...stav };
  if (o.typ_podnikani && !stav.typ_podnikani) dalsi.typ_podnikani = o.typ_podnikani;

  const pc = { ...(stav.profitCleaning || {}) };
  const mesicne = v => String(Math.round(Number(v) / 12));
  if (o.majitel_pracuje === true) {
    if (!prazdne(o.odmena_majitele) && prazdne(pc.owner_pay)) pc.owner_pay = mesicne(o.odmena_majitele);
    if (!prazdne(o.trzni_mzda) && prazdne(pc.market_replacement)) {
      pc.market_replacement = mesicne(o.trzni_mzda);
      pc.market_replacement_touched = true;
    }
    if (!prazdne(o.osobni_vydaje) && prazdne(pc.personal_costs)) pc.personal_costs = String(o.osobni_vydaje);
  }
  if (!prazdne(o.jednorazove_naklady) && prazdne(pc.one_time_costs)) pc.one_time_costs = String(o.jednorazove_naklady);
  if (!prazdne(o.jednorazove_vynosy) && prazdne(pc.one_time_income)) pc.one_time_income = String(o.jednorazove_vynosy);
  dalsi.profitCleaning = pc;

  const fin = { ...(stav.finance || {}) };
  if (!prazdne(o.trzby_minuly_rok) && prazdne(fin.revenue_prev_year)) fin.revenue_prev_year = Number(o.trzby_minuly_rok);
  if (!prazdne(o.uvery) && prazdne(fin.interest_bearing_debt)) fin.interest_bearing_debt = Number(o.uvery);
  dalsi.finance = fin;
  return dalsi;
}

/** Návrh hodnoty podle metody, s násobkem z valuačního jádra. */
export async function navrhHodnoty({ vykaz, typPodnikani, odpovedi, valuationRequest }) {
  const res = await fetch(`${API_BASE}/api/navrh`, {
    method: 'POST',
    headers: await authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      vykaz: vykaz || null,
      typ_podnikani: typPodnikani || null,
      odpovedi: odpovedi || {},
      valuation_request: valuationRequest || null,
    }),
  });
  if (!res.ok) throw await chybaZOdpovedi(res);
  return res.json();
}

/**
 * Úpravy zisku, se kterými počítal výpočet: platí krok Očištění zisku,
 * ne původní odpovědi z rozboru (klient je tam mohl změnit). Ostatní
 * rozdíl ukáže návrh jako „Další úpravy zisku ve výpočtu“.
 */
export function upravyZOcisteni(pc) {
  const c = v => (Number(v) > 0 ? Number(v) : null);
  const p = pc || {};
  return {
    jednorazove_naklady: c(p.one_time_costs),
    jednorazove_vynosy: c(p.one_time_income),
    odmena_majitele: !p.skipped && p.owner_choice === 'owner' ? c(Number(p.owner_pay) * 12) : null,
  };
}

/** Výkaz z rozboru a odpovědi s úpravami zisku, jak je bere návrh i zpráva. */
export function podkladyNavrhu(wizardState) {
  const rozbor = wizardState.zadani && wizardState.zadani.rozbor;
  return {
    vykaz: rozbor ? rozbor.vykaz : null,
    odpovedi: { ...(rozbor ? rozbor.odpovedi : {}), ...upravyZOcisteni(wizardState.profitCleaning) },
  };
}
